import React, { useState, useEffect, useCallback } from "react";
import { Wallet, TrendingUp, PieChart, Percent, Lock, Trophy } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Card, Button, Badge, Spinner, PageHeader } from "../../lib/ui";
import { useToast } from "../../lib/toast";
import api from "../../lib/api";
import { rp, pct, PeriodPicker, periodLabel } from "../../components/finance";

function Line2({ label, value, tone, strong, testid }) {
  const cls = tone === "danger" ? "text-danger-text" : tone === "success" ? "text-success-text" : "text-ink";
  return (
    <div className="flex items-center justify-between border-b border-line/50 py-1.5 last:border-0">
      <span className="text-sm text-ink-soft">{label}</span>
      <span className={`text-sm ${strong ? "font-extrabold" : "font-semibold"} ${cls}`} data-testid={testid}>{value}</span>
    </div>
  );
}

function BigKpi({ icon: Icon, label, value, sub, tone = "primary", testid }) {
  const toneBg = { primary: "bg-primary/10 text-primary", success: "bg-emerald-500/10 text-emerald-600", danger: "bg-danger/10 text-danger-text", ink: "bg-ink/5 text-ink" }[tone];
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-ink-mute"><div className={`flex h-8 w-8 items-center justify-center rounded-lg ${toneBg}`}><Icon size={16} /></div><span className="text-xs font-bold uppercase tracking-wide">{label}</span></div>
      <p className="mt-2 font-heading text-2xl font-extrabold text-ink">{value}</p>
      {sub != null && <p className="text-xs text-ink-mute">{sub}</p>}
    </Card>
  );
}

export default function FinanceLiteDashboard() {
  const toast = useToast();
  const [period, setPeriod] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/finance-lite/dashboard", { params: { period } }); setData(data); }
    catch { toast.error("Gagal memuat dashboard"); }
    finally { setLoading(false); }
  }, [period, toast]);
  useEffect(() => { load(); }, [load]);

  const s = data?.summary;
  const rev = s?.revenue; const cost = s?.cost; const profit = s?.profit; const ratios = s?.ratios;

  return (
    <div data-testid="finance-lite-dashboard-page">
      <PageHeader title="Dashboard Keuangan (Finance Lite)" subtitle="Estimasi Laba Rugi bulanan otomatis dari data operasional" icon={PieChart}>
        <PeriodPicker value={period} onChange={setPeriod} />
        {data?.closed && <Badge tone="warn" data-testid="fl-frozen-badge"><Lock size={13} /> Bulan Ditutup</Badge>}
      </PageHeader>

      {loading || !data ? <div className="flex h-64 items-center justify-center"><Spinner className="h-8 w-8" /></div> : (
        <div className="space-y-5">
          {/* Headline profit KPIs */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <BigKpi icon={Wallet} label="Penjualan Bersih" value={rp(rev?.net_sales)} sub={`Bill ${rp(rev?.bill_revenue)}`} tone="ink" testid="fl-kpi-net-sales" />
            <BigKpi icon={TrendingUp} label="Laba Kotor" value={rp(profit?.gross_profit)} sub={`${profit?.gross_margin_pct ?? "—"}%`} tone="primary" testid="fl-kpi-gross-profit" />
            <BigKpi icon={TrendingUp} label="Laba Operasional" value={rp(profit?.operating_profit)} sub={`${profit?.operating_margin_pct ?? "—"}%`} tone="primary" testid="fl-kpi-op-profit" />
            <BigKpi icon={Percent} label="Estimasi Laba Bersih" value={rp(profit?.net_profit)} sub={`Margin ${profit?.net_margin_pct ?? "—"}%`} tone={(profit?.net_profit || 0) >= 0 ? "success" : "danger"} testid="fl-kpi-net-profit" />
          </div>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            {/* Revenue */}
            <Card className="p-5" data-testid="fl-revenue-card">
              <h3 className="mb-3 font-heading text-base font-bold text-ink">Pendapatan</h3>
              <Line2 label="Bill (sebelum pajak)" value={rp(rev?.bill_revenue)} strong testid="fl-bill-revenue" />
              <Line2 label="Service Charge" value={`+ ${rp(rev?.service_charge)}`} tone="success" testid="fl-service" />
              <Line2 label="Biaya Marketplace" value={`- ${rp(rev?.marketplace_fee)}`} tone="danger" testid="fl-marketplace" />
              <Line2 label="Penjualan Bersih (Net Revenue)" value={rp(rev?.net_sales)} strong tone="success" testid="fl-net-sales" />
              <div className="mt-2 rounded-xl bg-amber-50 p-3">
                <Line2 label="PB1 (titipan pajak — bukan pendapatan)" value={rp(rev?.pb1)} tone="danger" testid="fl-pb1" />
                <Line2 label="Total Dibayar Pelanggan" value={rp(rev?.customer_payment)} testid="fl-customer-payment" />
              </div>
              <div className="mt-2 rounded-xl bg-ink/5 p-3">
                <Line2 label="Pendapatan Food" value={rp(rev?.food_revenue)} testid="fl-food-rev" />
                <Line2 label="Pendapatan Beverage" value={rp(rev?.beverage_revenue)} testid="fl-bev-rev" />
              </div>
            </Card>

            {/* Cost */}
            <Card className="p-5" data-testid="fl-cost-card">
              <h3 className="mb-3 font-heading text-base font-bold text-ink">Biaya</h3>
              <Line2 label="COGS Food" value={rp(cost?.food_cogs)} testid="fl-food-cogs" />
              <Line2 label="COGS Beverage" value={rp(cost?.beverage_cogs)} testid="fl-bev-cogs" />
              <Line2 label="Waste (Barang Rusak)" value={rp(cost?.waste)} testid="fl-waste" />
              <Line2 label="Kerugian Insiden" value={rp(cost?.incident_loss)} testid="fl-incident" />
              <Line2 label="Payroll (dari HR)" value={rp(cost?.payroll)} testid="fl-payroll" />
              <Line2 label="Biaya Operasional" value={rp(cost?.operating_expenses)} testid="fl-opex" />
              <Line2 label="Biaya Pembayaran (MDR)" value={rp(cost?.payment_fee)} strong testid="fl-payment-fee" />
            </Card>

            {/* Ratios */}
            <Card className="p-5" data-testid="fl-ratios-card">
              <h3 className="mb-3 font-heading text-base font-bold text-ink">Rasio Kinerja</h3>
              <Line2 label="Food Cost %" value={pct(ratios?.food_cost_pct)} testid="fl-food-pct" />
              <Line2 label="Beverage Cost %" value={pct(ratios?.beverage_cost_pct)} testid="fl-bev-pct" />
              <Line2 label="Payroll %" value={pct(ratios?.payroll_pct)} testid="fl-payroll-pct" />
              <Line2 label="Operating Expense %" value={pct(ratios?.opex_pct)} testid="fl-opex-pct" />
              <Line2 label="Payment Fee %" value={pct(ratios?.payment_fee_pct)} testid="fl-payment-pct" />
              <Line2 label="Net Margin %" value={pct(profit?.net_margin_pct)} strong tone="success" testid="fl-net-margin-pct" />
            </Card>
          </div>

          {/* Branch comparison */}
          <Card className="overflow-hidden p-0" data-testid="fl-comparison-card">
            <div className="flex items-center gap-2 border-b border-line px-5 py-3"><Trophy size={16} className="text-primary" /><h3 className="font-heading text-sm font-bold text-ink">Perbandingan Cabang</h3></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-left text-xs font-bold uppercase tracking-wide text-ink-mute">
                  <tr><th className="px-5 py-2">Cabang</th><th className="px-3 py-2 text-right">Penjualan Bersih</th><th className="px-3 py-2 text-right">Laba Kotor</th><th className="px-3 py-2 text-right">Laba Bersih</th><th className="px-3 py-2 text-right">Margin</th><th className="px-3 py-2 text-right">Food%</th><th className="px-5 py-2 text-right">Payroll%</th></tr>
                </thead>
                <tbody>
                  {data.comparison?.map((b, i) => (
                    <tr key={b.branch_id} className="border-t border-line/60" data-testid={`fl-cmp-${b.branch_id}`}>
                      <td className="px-5 py-2 font-semibold text-ink">{i === 0 && <Trophy size={12} className="mr-1 inline text-amber-500" />}{b.branch_name}</td>
                      <td className="px-3 py-2 text-right text-ink-soft">{rp(b.net_sales)}</td>
                      <td className="px-3 py-2 text-right text-ink-soft">{rp(b.gross_profit)}</td>
                      <td className={`px-3 py-2 text-right font-bold ${(b.net_profit || 0) >= 0 ? "text-success-text" : "text-danger-text"}`}>{rp(b.net_profit)}</td>
                      <td className="px-3 py-2 text-right text-ink-soft">{pct(b.net_margin_pct)}</td>
                      <td className="px-3 py-2 text-right text-ink-soft">{pct(b.food_cost_pct)}</td>
                      <td className="px-5 py-2 text-right text-ink-soft">{pct(b.payroll_pct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Trend charts */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <Card className="p-5" data-testid="fl-trend-sales">
              <h3 className="mb-3 font-heading text-sm font-bold text-ink">Tren Penjualan & Laba</h3>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={data.trend?.map((t) => ({ ...t, label: periodLabel(t.period) }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="label" fontSize={11} /><YAxis fontSize={11} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}jt`} />
                  <Tooltip formatter={(v) => rp(v)} />
                  <Legend />
                  <Line type="monotone" dataKey="net_sales" name="Penjualan Bersih" stroke="#f97316" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="gross_profit" name="Laba Kotor" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="net_profit" name="Laba Bersih" stroke="#10b981" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
            <Card className="p-5" data-testid="fl-trend-ratios">
              <h3 className="mb-3 font-heading text-sm font-bold text-ink">Tren Rasio Biaya (%)</h3>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={data.trend?.map((t) => ({ ...t, label: periodLabel(t.period) }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="label" fontSize={11} /><YAxis fontSize={11} unit="%" />
                  <Tooltip formatter={(v) => `${v ?? 0}%`} />
                  <Legend />
                  <Line type="monotone" dataKey="food_cost_pct" name="Food Cost %" stroke="#f97316" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="beverage_cost_pct" name="Beverage %" stroke="#a855f7" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="payroll_pct" name="Payroll %" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="opex_pct" name="OpEx %" stroke="#64748b" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
